import type { CalendarDate } from "@internationalized/date";
import { interpolate } from "@jetblue/kit-interpolation";
import type { BestFaresFocusState } from "../components/best-fares/use-best-fares-picker-state";
import type { TripType } from "../hooks/use-flight-search-form";
import type { DatePickerToolbarLabels } from "../labels";
import { TIMEZONE } from "../timezone";

type GetToolbarMessageInput = {
  focusState?: BestFaresFocusState;
  isError: boolean;
  isLoading: boolean;
  isRequestEnabled: boolean;
  labels: DatePickerToolbarLabels;
  selectedDepartDate?: CalendarDate;
  selectedReturnDate?: CalendarDate;
  tripType: Extract<TripType, "one-way" | "round-trip">;
  usePoints: boolean;
};

const DEPART_DATE_FORMATTER = new Intl.DateTimeFormat("en-US", {
  day: "numeric",
  month: "short",
  timeZone: TIMEZONE,
});

const getTripLength = (
  defaultTripLength: string,
  departDate?: CalendarDate,
  returnDate?: CalendarDate,
) => {
  if (!departDate || !returnDate) {
    return defaultTripLength;
  }

  const departJulianDay = departDate.calendar.toJulianDay(departDate);
  const returnJulianDay = returnDate.calendar.toJulianDay(returnDate);

  return String(Math.abs(returnJulianDay - departJulianDay));
};

export const getToolbarMessage = ({
  focusState = "none",
  isError,
  isLoading,
  isRequestEnabled,
  labels,
  selectedDepartDate,
  selectedReturnDate,
  tripType,
  usePoints,
}: GetToolbarMessageInput): string | undefined => {
  if (!isRequestEnabled || isLoading) {
    return undefined;
  }

  const {
    roundTripDefaultTripLength,
    roundTripServiceSuccessCashDepart,
    roundTripServiceSuccessCashReturn,
    roundTripServiceSuccessPointsDepart,
    roundTripServiceSuccessPointsReturn,
    serviceFail,
    serviceSuccessCash,
    serviceSuccessPoints,
  } = labels;

  if (isError) {
    return serviceFail;
  }

  if (tripType === "one-way") {
    return usePoints ? serviceSuccessPoints : serviceSuccessCash;
  }

  const tripLength = getTripLength(
    roundTripDefaultTripLength,
    selectedDepartDate,
    selectedReturnDate,
  );
  if (focusState === "end" && selectedDepartDate) {
    const template = usePoints
      ? roundTripServiceSuccessPointsReturn
      : roundTripServiceSuccessCashReturn;

    return interpolate(template, {
      departDate: DEPART_DATE_FORMATTER.format(
        selectedDepartDate.toDate(TIMEZONE),
      ),
    });
  }

  const template = usePoints
    ? roundTripServiceSuccessPointsDepart
    : roundTripServiceSuccessCashDepart;

  return interpolate(template, {
    tripLength,
  });
};
